import { useState, useCallback, useEffect } from 'react';

/**
 * useAnonFollows — localStorage-backed anonymous email subscription tracking
 *
 * Mirrors useFollows.js module-level shared state pattern so all hook instances
 * stay in sync. When an anonymous user subscribes via email, saves { targetType,
 * targetId, label } to localStorage. The dashboard reads this to show "Your Alerts".
 */

const STORAGE_KEY = 'gis_email_subscriptions';

// Shared state — single source of truth across all hook instances
let _anonFollows = [];
const _anonListeners = new Set();

function _notify() {
  _anonListeners.forEach(fn => fn([..._anonFollows]));
}

function _loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) _anonFollows = JSON.parse(raw);
    else _anonFollows = [];
  } catch {
    _anonFollows = [];
  }
}

function _saveToStorage() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(_anonFollows));
  } catch { /* quota exceeded — silent */ }
}

// Initialize on module load
_loadFromStorage();

export function useAnonFollows() {
  const [anonFollows, setAnonFollows] = useState(_anonFollows);

  useEffect(() => {
    _anonListeners.add(setAnonFollows);
    setAnonFollows([..._anonFollows]);
    return () => _anonListeners.delete(setAnonFollows);
  }, []);

  const addAnonFollow = useCallback((targetType, targetId, label) => {
    const exists = _anonFollows.some(f => f.targetType === targetType && f.targetId === targetId);
    if (exists) return;
    _anonFollows = [{ targetType, targetId, label, subscribedAt: new Date().toISOString() }, ..._anonFollows];
    _saveToStorage();
    _notify();
  }, []);

  const removeAnonFollow = useCallback((targetType, targetId) => {
    _anonFollows = _anonFollows.filter(f => !(f.targetType === targetType && f.targetId === targetId));
    _saveToStorage();
    _notify();
  }, []);

  const isAnonFollowing = useCallback((targetType, targetId) => {
    return _anonFollows.some(f => f.targetType === targetType && f.targetId === targetId);
  }, [anonFollows]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearAnonFollows = useCallback(() => {
    _anonFollows = [];
    _saveToStorage();
    _notify();
  }, []);

  return { anonFollows, addAnonFollow, removeAnonFollow, isAnonFollowing, clearAnonFollows };
}
